import { useState } from 'react';

import { useSplitTestBranch } from '@api/lendingAPI/lapiHooks';
import { getCountry } from '@iwIntlProvider/CountryProvider';
import { DEOnly } from '@iwIntlProvider/CountryWrappers';
import { IconLink } from '@iwoca/icons';
import {
    fetchGetStateByStateKey,
    fetchPutState,
    PutStateRequestBody
} from '@iwoca/lapi-client/edge';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Label, Select, Text } from '@ui';
import { produce } from 'immer';
import { FormattedMessage } from 'react-intl';

import * as IwDataDogLogger from '../../../../../../iwLogger/IwDataDogLogger';
import { useStateKey } from '../../../../../../iwQueryClient/useStateKey';
import DocumentRequirement from '../../DocumentRequirement/DocumentRequirement';

const ManualUpload = ({
    toggleLinkBankAccount
}: {
    toggleLinkBankAccount?: () => void;
}) => {
    const stateKey = useStateKey();

    const { branch } = useSplitTestBranch({
        test: 'de_6m_pdf_bs_test_2026_q2',
        suspense: false
    });
    const bankStatementDays = branch === 'INCLUDE' ? 180 : 90;
    const [showManualUploadButton, setShowManualUploadButton] = useState(false);

    const { data: customerState } = useQuery({
        queryKey: ['customer_state', stateKey],
        queryFn: () => fetchGetStateByStateKey({ stateKey })
    });

    const queryClient = useQueryClient();

    const saveCustomerState = useMutation({
        mutationFn: (state: PutStateRequestBody) =>
            fetchPutState({
                stateKey: stateKey,
                body: state
            }),
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey: ['customer_state', stateKey]
            });
        }
    });

    const submitManualReason = async (manualReasonData: string) => {
        const newState = produce(customerState, (draft) => {
            //@ts-expect-error:UI not on type definition
            if (draft?.data.ui) {
                //@ts-expect-error:UI not on type definition
                draft.data.ui = {
                    //@ts-expect-error:UI not on type definition
                    ...draft.data.ui,
                    OB_not_linked_reason: manualReasonData
                };
            } else {
                draft!.data = {
                    ...draft?.data,
                    //@ts-expect-error:UI not on type definition
                    ui: {
                        OB_not_linked_reason: manualReasonData
                    }
                };
            }
        });

        try {
            await saveCustomerState.mutateAsync(newState);
        } catch (error: any) {
            IwDataDogLogger.logError(
                error.message,
                {
                    tags: {
                        reporter: 'save_customer_state_manual_upload_documents'
                    }
                },
                error
            );
        }
    };

    const manualUploadReasons = [
        {
            value: 1,
            text: 'Ich habe kein Online Banking.'
        },
        {
            value: 2,
            text: 'Ich finde PDF hochladen einfacher.'
        },
        {
            value: 3,
            text: 'Ich konnte meine Bank nicht finden.'
        },
        {
            value: 4,
            text: 'Ich vertraue der Open Banking Lösung nicht.'
        },
        {
            value: 5,
            text: 'Ich finde, dass ich mit Open Banking mehr Informationen teile als notwendig.'
        },
        {
            value: 6,
            text: 'Ich möchte die Frage nicht beantworten.'
        }
    ];

    return (
        <DocumentRequirement
            documentType="bank statement"
            className="!p-0"
            disabled={!showManualUploadButton}
        >
            <ul className="list-disc">
                <li>
                    <FormattedMessage
                        id="_.apply_flexi_loan.newDocUploads.BankStatements.manualUpload.first_bullet"
                        defaultMessage="We accept PDF statements."
                    />
                </li>
                <li>
                    {/* eslint-disable formatjs/enforce-placeholders -- DE translation uses <strong>, EN does not */}
                    <FormattedMessage
                        id="_.apply_flexi_loan.newDocUploads.BankStatements.manualUpload.second_bullet"
                        defaultMessage="Your transactions must cover the stated period and be from the accounts(s) where the majority of your business revenue is generated"
                        values={{
                            strong: (chunks) => <strong>{chunks}</strong>
                        }}
                    />
                    {/* eslint-enable formatjs/enforce-placeholders */}
                </li>
                {getCountry() === 'DE' && (
                    <>
                        <li>
                            <FormattedMessage
                                id="_.apply_flexi_loan.newDocUploads.BankStatements.manualUpload.point3"
                                defaultMessage="Your transactions must cover the stated period and be from the accounts(s) where the majority of your business revenue is generated"
                            />
                        </li>
                        <li>
                            <FormattedMessage
                                id="_.apply_flexi_loan.newDocUploads.BankStatements.manualUpload.point4"
                                defaultMessage="Your transactions must cover the stated period and be from the accounts(s) where the majority of your business revenue is generated"
                            />
                        </li>
                        <li>
                            <FormattedMessage
                                id="_.apply_flexi_loan.newDocUploads.BankStatements.manualUpload.point5"
                                defaultMessage="Your transactions must cover the stated period ({days} days) and be from the accounts(s) where the majority of your business revenue is generated"
                                values={{ days: bankStatementDays }}
                            />
                        </li>
                        <li>
                            <FormattedMessage
                                id="_.apply_flexi_loan.newDocUploads.BankStatements.manualUpload.point6"
                                defaultMessage="Your transactions must cover the stated period and be from the accounts(s) where the majority of your business revenue is generated"
                            />
                        </li>
                    </>
                )}
            </ul>
            <DEOnly>
                <div className="pt-xl text-primary-10">
                    <Label htmlFor="manual-bank-statement-reason">
                        <FormattedMessage
                            id="_.apply_flexi_loan.newDocUploads.BankStatements.selectOptionLabel"
                            defaultMessage="Select an option below to manually upload bank statements"
                        />
                    </Label>
                    <Select
                        className="mt-s w-3/4"
                        defaultValue="none"
                        name="manual-bank-statement-reason"
                        onChange={(e) => {
                            if (e.target.value !== 'none') {
                                setShowManualUploadButton(true);
                            }
                        }}
                        onBlur={(e) => submitManualReason(e.target.value)}
                    >
                        <optgroup label="Ich kann mein Bankkonto nicht verknüpfen">
                            <option disabled hidden value="none">
                                Bitte wählen Sie eine Option aus
                            </option>
                            {manualUploadReasons.map((manualreason) => (
                                <option
                                    key={manualreason.value}
                                    value={manualreason.text}
                                >
                                    {manualreason.text}
                                </option>
                            ))}
                        </optgroup>
                    </Select>
                </div>
            </DEOnly>
            {toggleLinkBankAccount && (
                <div className="mt-l flex items-start">
                    <IconLink className="mr-s mt-xs shrink-0 fill-structure-40" />
                    <div className="inline-flex flex-wrap">
                        <Text className="text-structure-40">
                            <FormattedMessage
                                id="_.apply_flexi_loan.newDocUploads.BankStatements.manualUpload.link_true_layer"
                                defaultMessage="We also offer Open Banking, a secure way to share your business banking data without sharing your password."
                            />
                        </Text>
                        <button
                            onClick={toggleLinkBankAccount}
                            className="border-none bg-transparent px-0 font-med text-primary-40 underline hover:cursor-pointer"
                        >
                            <FormattedMessage
                                id="_.apply_flexi_loan.newDocUploads.BankStatements.manualUpload.link_true_layer_cta"
                                defaultMessage="Link my bank account"
                            />
                        </button>
                    </div>
                </div>
            )}
        </DocumentRequirement>
    );
};

// eslint-disable-next-line import/no-default-export
export default ManualUpload;
